/* ============================================================================
   Phoniq Insight · typed API client
   Wraps the live phoniq backend. In dev, VITE_API_BASE=/api is proxied to
   http://localhost:8000 (see vite.config.ts). Everything is GET + JSON.

   Design note: every figure we render is anchored two ways · temporally
   (an "as of" from data_freshness) and comparatively (a baseline/peer band).
   The types below carry both so the UI never shows a naked number.
   ========================================================================== */

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";

const cache = new Map<string, Promise<unknown>>();

async function get<T>(path: string): Promise<T> {
  if (!cache.has(path)) {
    const p = fetch(BASE + path, { headers: { accept: "application/json" } }).then((r) => {
      if (!r.ok) throw new ApiError(r.status, path);
      return r.json();
    });
    cache.set(path, p);
    // Drop failed promises so a transient error can be retried.
    p.catch(() => cache.delete(path));
  }
  return cache.get(path) as Promise<T>;
}

/** Memoise a derived index (fetch + reshape) for the page's lifetime, but drop
    the memo if it rejects, so one transient API failure doesn't poison the
    index forever — the next caller retries. `get()` above does the same for the
    raw fetch; without this the reshaped promise outlives it and every later
    read re-awaits the same stale rejection. */
function once<T>(make: () => Promise<T>): () => Promise<T> {
  let p: Promise<T> | null = null;
  return () => {
    if (!p) {
      p = make();
      p.catch(() => {
        p = null;
      });
    }
    return p;
  };
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public path: string,
  ) {
    super(`API ${status} on ${path}`);
  }
}

/* ---- Search / operators -------------------------------------------------- */

export interface OperatorHit {
  id: string;
  slug: string;
  name: string;
  org_nr: string;
  entity_type: string;
  county: string | null;
  municipality: string | null;
  n_sites: number;
  n_licenses: number;
}

export interface OperatorSite {
  locality_no: number;
  locality: string;
  municipality: string | null;
  lon: number;
  lat: number;
  licenses: string[];
  production_stages: string[];
  has_active: boolean;
  active_capacity: string | null;
  capacity_unit: string | null;
}

export interface OperatorDetail extends Omit<OperatorHit, "n_sites" | "n_licenses"> {
  website: string | null;
  first_seen_at: string;
  last_seen_at: string;
  sites: OperatorSite[];
}

export function searchOperators(q: string, limit = 8): Promise<{ items: OperatorHit[] }> {
  return get(`/v1/operators?q=${encodeURIComponent(q)}&only_with_sites=true&limit=${limit}`);
}

export function getOperator(slug: string): Promise<OperatorDetail> {
  return get(`/v1/operators/${slug}`);
}

/* ---- Connections (company constellation) --------------------------------- */

export interface JointFarmLink {
  slug: string;
  name: string;
  org_nr: string;
  shared_sites: number;
}
export interface VesselCompanyLink {
  company: string;
  org_nr: string | null;
  vessels: number;
}
export interface FishDestinationLink {
  name: string;
  org_nr: string | null;
  deliveries: number;
}
export interface OperatorConnections {
  operator: { slug: string; name: string };
  joint_farms: JointFarmLink[];
  vessel_companies: VesselCompanyLink[];
  fish_destinations: FishDestinationLink[];
}

export function getOperatorConnections(slug: string): Promise<OperatorConnections> {
  return get(`/v1/operators/${slug}/connections`);
}

/** Full org_nr -> operator index (all operators with sites), cached once.
    Needed because slugs mangle non-ASCII (ø -> "-") and can't be constructed,
    and the search `q` param doesn't match on org number. */
export const getOperatorIndex = once<Map<string, OperatorHit>>(() =>
  get<{ items: OperatorHit[] }>(`/v1/operators?only_with_sites=true&limit=2000`).then(
    (d) => new Map(d.items.map((o) => [o.org_nr, o])),
  ),
);

/* ---- Portfolio rollup ---------------------------------------------------- */

export interface OperatorSummary {
  operator: { id: string; slug: string; name: string; org_nr: string };
  window: { from: string; to: string };
  n_localities: number;
  noise: {
    n_buckets: number;
    n_receivers: number;
    mean_db: number;
    p50_db: number;
    p95_db: number;
    max_db: number;
    min_db: number;
    energy_avg_db: number;
  };
  lice: {
    n_observations: number;
    n_localities_reported: number;
    mean_af_lice: number;
    p50_af_lice: number;
    p95_af_lice: number;
    n_pd_weeks: number;
    n_ila_weeks: number;
    n_fallow_weeks: number;
  };
  traffic: {
    year: number;
    buffer_m: number;
    n_localities: number;
    total_value: number;
    mean_value: number;
    max_value: number;
    noise_weighted_value: number;
  };
}

export function getSummary(slug: string, latest = "30d"): Promise<OperatorSummary> {
  return get(`/v1/operators/${slug}/summary?latest=${latest}`);
}

/* ---- Production cycle (whole-country; filter client-side) ---------------- */

export interface CycleItem {
  locality_no: number;
  stage: string; // ung | vekst | moden | slakteklar | brakk | ukjent | annen_art
  age_months: number | null;
  cohort_year: number | null;
  cohort_week: number | null;
  status: string | null;
  capacity_t: number | null;
  po_id: number | null;
  est_biomass_t: number | null;
  harvest_year: number | null;
  harvest_month: number | null;
  harvest_t: number | null;
  // Licensed species string + a single category code. "laksefisk" for
  // salmon-reported sites; otherwise torsk | marin_fisk | rensefisk | roye |
  // skjell | tare | annet — these are outside the salmon fallow/age regime.
  species?: string | null;
  species_category?: string | null;
}

export interface CodNational {
  latest: { year: number; month: number; biomass_t: number; fish_count: number; harvest_t: number } | null;
  n_localities: number;
  series: { year: number; month: number; biomass_t: number; fish_count: number; harvest_t: number }[];
}

export const getCodNational = once<CodNational>(() => get<CodNational>(`/v1/cod/national`));

let cycleAsOf = "";

export const getCycleMap = once<Map<number, CycleItem>>(() =>
  get<{ as_of: string; items: CycleItem[] }>(`/v1/farms/cycle`).then((d) => {
    cycleAsOf = d.as_of;
    return new Map(d.items.map((i) => [i.locality_no, i]));
  }),
);
export function cycleAsOfDate(): string {
  return cycleAsOf;
}

/* ---- Akva report (the rich per-site narrative source) -------------------- */

export interface AkvaLocality {
  locality_no: number;
  name: string;
  municipality: string | null;
  lat: number;
  lon: number;
  capacity_tn: number | null;
  weeks_in_window: number;
  weeks_with_fish: number;
  weeks_fallow: number;
  avg_lice: number | null;
  max_lice: number | null;
  lice_action_threshold: number | null;
  over_threshold: boolean;
  percentile_in_band: number | null;
  weeks_substance: number;
  weeks_mechanical: number;
  weeks_cleanerfish: number;
  weeks_pd: number;
  weeks_ila: number;
  chronic_pdila_months: number | null;
  production_area_id: number | null;
  production_area_name: string | null;
  production_area_color: string | null; // red | yellow | green
  restriction_zones: unknown[];
  sea_temperature_mean_c: number | null;
  latest_env_report: { type: string; summary: string } | null;
  worst_site_condition_12m: number | null;
  escapes_in_window: number;
  escapes_last_12m: number;
  latest_escape: unknown;
  ais_unique_vessels: number | null;
  ais_closest_pass_m: number | null;
  ais_positions: number | null;
  noise_mean_db: number | null;
  noise_p95_db: number | null;
  noise_energy_avg_db: number | null;
  noise_max_db: number | null;
  hs_mean_m: number | null;
  hs_p95_m: number | null;
  hs_max_m: number | null;
  frac_storm: number | null;
  depth_mean_m: number | null;
  depth_max_m: number | null;
  flags: string[];
}

export interface AkvaReport {
  operator: { name: string; org_nr: string; county: string | null };
  year: number;
  month: number;
  window_start: string;
  window_end: string;
  n_localities: number;
  n_active: number;
  n_fallow: number;
  total_active_capacity_tn: number | null;
  capacity_unit: string | null;
  localities: AkvaLocality[];
  headline_actions: string[];
  data_freshness: Record<string, string | number>;
  known_gaps: string[];
}

export function getAkvaReport(slug: string): Promise<AkvaReport> {
  return get(`/v1/operators/${slug}/akva-report`);
}

/* ---- Price + FX (value math) -------------------------------------------- */

export interface PriceModel {
  unit: string;
  anchor: number; // NOK/kg, recent
  as_of: string;
  seasonal_index: Record<string, number>;
  backtest_mape: Record<string, number>;
  history?: unknown[];
  forecast?: { month: number; year: number; price: number }[];
}
export function getPrice(): Promise<PriceModel> {
  return get(`/v1/salmon/price`);
}

export interface FxImplied {
  nok_rate: number | null;
  usd_rate: number | null;
  implied_nok_pct: number | null;
  note: string;
}
export function getFx(): Promise<FxImplied> {
  return get(`/v1/fx/implied`);
}

/* ---- Vessel activity at a receiver --------------------------------------- */

export interface ReceiverLite {
  id: string;
  kind: string;
  name: string;
  locality_no: number | null;
  municipality: string | null;
  lon: number;
  lat: number;
  cage_count?: number | null;
}

export interface VesselContact {
  mmsi: number;
  name: string | null;
  role: string | null; // wellboat | carrier | service
  op: string | null; // delousing | harvest | feed | ...
  op_label: string | null; // human label (Norwegian)
  dwell_min: number | null; // span between first/last ping near the cage
  frac_stopped: number | null; // 0..1 share of pings under 0.5 kn
  last_ts: string | null;
}

export interface VesselActivity {
  receiver_id: string;
  days: number;
  n_vessels: number;
  by_role: Record<string, number>;
  by_op: Record<string, number>;
  vessels: VesselContact[];
}

/** A vessel's recent path as a GeoJSON Feature (MultiLineString). */
export interface VesselTrack {
  type: "Feature";
  geometry: { type: "MultiLineString"; coordinates: [number, number][][] } | null;
  properties: {
    mmsi: number;
    days: number;
    points: number;
    first_ts: string | null;
    last_ts: string | null;
  };
}

export function getVesselTrack(mmsi: number, days = 14): Promise<VesselTrack> {
  return get(`/v1/vessels/${mmsi}/track?days=${days}`);
}

/** Precomputed REAL track clip(s) for one operation event (time-machine replay).
    Points are [lon, lat, ts(iso), sog], decimated + ordered by time. */
export interface EventTrack {
  mmsi: number;
  name: string | null;
  points: [number, number, string, number][];
}
/** Off-site endpoint of an operation: where a harvest hauler took the fish (a
    slaughterhouse, role 'destination') or where a smolt hauler loaded it (a
    settefisk site, role 'origin'). Drives the time-machine's pin + camera reveal.
    `accl_mode` (origin only): 'in_transit' = gradual seawater acclimatization on
    the long voyage; 'direct' = short delivery. */
export interface EventEndpoint {
  mmsi: number;
  role: "origin" | "destination";
  ref_kind: "settefisk" | "plant" | "ensilage_plant";
  ref_id: string;
  name: string;
  lon: number;
  lat: number;
  ts: string | null;
  travel_h: number | null;
  accl_mode: "direct" | "in_transit" | null;
}
export interface EventTracks {
  locality_no: number;
  date: string;
  tracks: EventTrack[];
  endpoints?: EventEndpoint[];
}
export function getEventTrack(
  localityNo: number,
  date: string,
  mmsi?: number,
): Promise<EventTracks> {
  const q = mmsi != null ? `&mmsi=${mmsi}` : "";
  return get(`/v1/receivers/${localityNo}/event-track?date=${encodeURIComponent(date)}${q}`);
}

export interface VesselChainStop {
  kind: "farm" | "plant" | "service" | "holding";
  name: string | null;
  ref: number | string | null; // locality_no (farm) | godkjenningsnr (plant) | null
  linkable: boolean;
  label: string;
  op_type?: string | null;
  klass?: string | null;
  municipality?: string | null;
  started_at: string;
  ended_at: string;
  dwell_min: number;
  lon: number;
  lat: number;
}
export interface VesselChain {
  mmsi: number;
  days: number;
  n_stops: number;
  stops: VesselChainStop[];
}
export function getVesselChain(mmsi: number, days = 14): Promise<VesselChain> {
  return get(`/v1/vessels/${mmsi}/chain?days=${days}`);
}

/* ---- Vessel-centric (the vessel page) ----------------------------------- */

export interface VesselFeedItem {
  mmsi: number;
  name: string | null;
  role: string | null;
  state: string | null; // transit | reception | farm | service | idle
  at_facility: string | null; // named slaughter plant when state=reception
  laden: boolean | null;
  length_m: number | null;
  ship_type: number | null;
  sog: number | null;
  cog: number | null;
  ts: string | null;
  age_h: number | null;
  last_op: string | null;
  last_op_at: string | null;
  operators: { name?: string; slug?: string }[] | string[] | null;
  from_site: string | null;
  from_operator: string | null;
  // Owner (NOR/NIS register, joined by call sign).
  owner: string | null;
  owner_org: string | null;
  owner_role: string | null; // Eier | Driftsansvarlig | ISM-ansvarlig
  registered_owner: string | null;
  operator_company: string | null;
  register: string | null; // NOR | NIS
  vessel_type: string | null; // authoritative classification, e.g. "BRØNNFARTØY"
  lon: number | null; // last known position (from feed geometry)
  lat: number | null;
}

/** The public aqua-fleet positions feed (GeoJSON), cached + indexed by mmsi. */
export const getVesselFeed = once<VesselFeedItem[]>(() =>
  get<{
    features: { properties: VesselFeedItem; geometry: { coordinates: [number, number] } }[];
  }>(`/v1/vessels/positions`).then((d) =>
    d.features.map((f) => ({
      ...f.properties,
      lon: f.geometry?.coordinates?.[0] ?? null,
      lat: f.geometry?.coordinates?.[1] ?? null,
    })),
  ),
);

export async function getVesselById(mmsi: number): Promise<VesselFeedItem | null> {
  const feed = await getVesselFeed();
  return feed.find((v) => v.mmsi === mmsi) ?? null;
}

export interface VesselLocality {
  locality_no: number;
  name: string;
  municipality: string | null;
  lon: number;
  lat: number;
  pings: number;
  slow_pings: number; // moored / <1 kn · "working here" signal
  first_seen: string | null;
  last_seen: string | null;
  closest_m: number | null;
  n_operations: number;
  last_op_type: string | null;
  operators: { name: string; slug: string }[];
}

export function getVesselLocalities(mmsi: number, since = "30d"): Promise<{ items: VesselLocality[] }> {
  return get(`/v1/vessels/${mmsi}/localities?since=${since}`);
}

/** Resolve receiver ids for an operator's localities via a bbox query. */
export async function getReceiversInBbox(
  minLon: number,
  minLat: number,
  maxLon: number,
  maxLat: number,
): Promise<Map<number, ReceiverLite>> {
  const bbox = `${minLon},${minLat},${maxLon},${maxLat}`;
  const d = await get<{ items: ReceiverLite[] }>(
    `/v1/receivers?kind=farm&bbox=${bbox}&limit=5000`,
  );
  const m = new Map<number, ReceiverLite>();
  for (const r of d.items) if (r.locality_no != null) m.set(r.locality_no, r);
  return m;
}

export function getVesselActivity(receiverId: string, days = 14): Promise<VesselActivity> {
  return get(`/v1/receivers/${receiverId}/vessel-activity?days=${days}`);
}

/* ---- Location / site page ----------------------------------------------- */

/** All farm receivers, indexed by locality_no (cached once). Also powers
    location search and the company-map → site links. */
export const getReceiverIndex = once<Map<number, ReceiverLite>>(() =>
  get<{ items: ReceiverLite[] }>(`/v1/receivers?kind=farm&limit=5000`).then((d) => {
    const m = new Map<number, ReceiverLite>();
    for (const r of d.items) if (r.locality_no != null) m.set(r.locality_no, r);
    return m;
  }),
);

export interface ReceiverStatus {
  receiver_id: string;
  locality_no: number;
  year: number;
  week: number;
  is_fallow: boolean;
  has_lice: boolean;
  has_treat: boolean;
  has_pd: boolean;
  has_ila: boolean;
  avg_lice: number | null;
  operator_slugs: string[];
  operator_names: string[];
}

export const getReceiverStatusMap = once<Map<number, ReceiverStatus>>(() =>
  get<{ items: ReceiverStatus[] }>(`/v1/receivers/status`).then(
    (d) => new Map(d.items.map((s) => [s.locality_no, s])),
  ),
);

export interface NoisePeakSource {
  mmsi: number;
  name: string | null;
  category: string | null;
  distance_m: number | null;
  speed_kn: number | null;
  level_db: number | null;
}
export interface ReceiverReport {
  receiver: { id: string; name: string; lat: number; lon: number; municipality: string | null };
  window: { from: string; to: string };
  summary: {
    n_buckets: number;
    mean_db: number;
    p50_db: number;
    p95_db: number;
    max_db: number;
    min_db: number;
    energy_avg_db: number;
  };
  peaks: {
    ts_bucket: string;
    total_db: number;
    n_sources: number;
    top_sources: NoisePeakSource[];
  }[];
}
export function getReceiverReport(receiverId: string, latest = "30d"): Promise<ReceiverReport> {
  return get(`/v1/receivers/${receiverId}/report?latest=${latest}`);
}

/* ---- Live noise (UNcached — polled by the realtime monitor) --------------- */

/** Bare fetch that bypasses the module cache. The realtime noise monitor polls
    these every few seconds, so caching would freeze the picture. */
async function live<T>(path: string): Promise<T> {
  const r = await fetch(BASE + path, { headers: { accept: "application/json" } });
  if (!r.ok) throw new ApiError(r.status, path);
  return r.json() as Promise<T>;
}

export interface NoisePeakBucket {
  ts_bucket: string;
  bucket_seconds?: number;
  total_db: number;
  n_sources: number;
  top_sources: NoisePeakSource[];
}
export interface ReceiverPeaks {
  receiver_id: string;
  items: NoisePeakBucket[];
}
/** Loudest buckets in the window, each with the top contributing vessels
    (distance, speed, per-vessel level) — the raw material for the near/far
    split in the live monitor. Uncached. */
export function getReceiverPeaksLive(
  receiverId: string,
  latest = "30m",
  top = 40,
): Promise<ReceiverPeaks> {
  return live(`/v1/receivers/${receiverId}/peaks?latest=${latest}&top=${top}`);
}

export interface NoiseHeatItem {
  receiver_id: string;
  name: string;
  lon: number;
  lat: number;
  db: number;
  n: number;
}
/** One row per receiver in the bbox, aggregated over the window — fast enough to
    flag currently-noisy sites on a landing page (one call, ~0.1s). Uncached so a
    reload reflects the live picture. */
export function getNoiseHeatmap(
  bbox: string,
  latest = "1h",
  aggregate = "max",
): Promise<{ items: NoiseHeatItem[] }> {
  return live(`/v1/noise/heatmap?bbox=${bbox}&latest=${latest}&aggregate=${aggregate}`);
}

export interface NoiseSeriesPoint {
  bucket: string;
  db: number;
  n: number;
  n_sources: number;
}
export interface ReceiverNoise {
  receiver_id: string;
  bucket: string;
  aggregate: string;
  items: NoiseSeriesPoint[];
}
/** Re-bucketed noise time series at one receiver — drives the live pulse and the
    last-hour sparkline. Uncached. */
export function getReceiverNoiseLive(
  receiverId: string,
  latest = "1h",
  bucket = "5m",
  aggregate = "esum",
): Promise<ReceiverNoise> {
  return live(
    `/v1/receivers/${receiverId}/noise?latest=${latest}&bucket=${bucket}&aggregate=${aggregate}`,
  );
}

export interface SiteLicense {
  license_nr: string;
  intention: string | null; // KOMMERSIELL | FoU | STAMFISK | …
  production_stage: string | null; // Matfisk | Settefisk
  capacity: number | null;
  capacity_unit: string | null;
  valid_from: string | null;
}
export interface SiteClassification {
  cage_shape: string | null;
  cage_count: number | null;
  layout: string | null;
  setting: string | null;
  terrain: string | null;
  image_quality: string | null;
  confidence: string | null;
  image_source: string | null;
  classified_at: string | null;
}
/** Higher-res Norge i bilder (NIB) ortophoto classification by a vision model.
 *  Richer than `SiteClassification`: counts rings, flags the feed barge
 *  separately, gives grid dimensions, and whether the stored coordinate is off
 *  the actual cages. */
export interface SiteClassificationNib {
  ring_count: number | null;
  cage_shape: string | null; // circular | square | closed | mixed | none
  has_feed_barge: boolean;
  layout: string | null; // line | grid | cluster | single | scattered | none
  grid_dims: string | null; // e.g. "3x2"
  setting: string | null;
  terrain: string | null;
  offset_from_centre: boolean;
  offset_direction: string | null;
  image_quality: string | null;
  confidence: string | null;
  notes: string | null;
  image_source: string | null;
  classified_at: string | null;
}
export interface NytekCert {
  inspection_body: string | null;
  status: string | null; // ISSUED | …
  issue_date: string | null;
  expiry_date: string | null;
  production_units_max: number | null;
  production_units_present: number | null;
  n_enclosures: number | null;
  n_floaters: number | null;
  n_rafts: number | null;
  n_mooring_systems: number | null;
  n_nonconformities: number | null;
  n_major_nonconformities: number | null;
  n_closed_majors: number | null;
  latest_mooring_inspection_date: string | null;
  latest_mooring_inspection_company: string | null;
}
export interface ReceiverDetail extends ReceiverLite {
  municipality_no: string | null;
  depth_mean_m: number | null;
  sea_temperature_c: number | null;
  classification: SiteClassification | null;
  classification_nib: SiteClassificationNib | null;
  licenses: SiteLicense[];
  nytek: NytekCert | null;
}
export function getReceiver(receiverId: string): Promise<ReceiverDetail> {
  return get(`/v1/receivers/${receiverId}`);
}

/** URL of the high-res NIB ortophoto for a site (box = ground footprint in m:
 *  500 tight / 1000 whole-farm / 2000 context). Same-origin via the API. */
export function nibImageUrl(receiverId: string, box: 500 | 1000 | 2000 = 1000): string {
  return `${BASE}/v1/receivers/${receiverId}/nib-image?box=${box}`;
}

/* ---- Onshore plants (land-based: slaughter / processing) ---------------- */

export type OnshoreKind = "slaughter_farmed" | "slaughter_marine" | "processing";

export interface OnshoreVessel {
  mmsi: number;
  name: string | null;
  role: string | null; // wellboat | carrier | service | tender
  length_m: number | null;
  n_pings: number;
  dwell_min: number | null;
  frac_stopped: number | null;
  closest_m: number | null;
  last_ts: string | null;
  from_site: string | null;
  from_locality_no: number | null;
  from_linkable: boolean;
  from_municipality: string | null;
  from_operator: string | null;
  from_operator_slug: string | null;
  from_lon: number | null;
  from_lat: number | null;
  harvest_ts: string | null;
}

export interface OnshoreOriginFarm {
  locality_no: number;
  name: string | null;
  municipality: string | null;
  linkable: boolean;
  operator: string | null;
  operator_slug: string | null;
  n_vessels: number;
}

export interface OnshoreOperator {
  slug: string;
  name: string | null;
  n_vessels: number;
}

export interface OnshoreDelivery {
  mmsi: number;
  vessel: string | null;
  from_site: string | null;
  from_locality_no: number | null;
  from_municipality: string | null;
  from_linkable: boolean;
  n: number;
  first: string;
  last: string;
}

export interface OnshoreHoldingCage {
  locality_no: number;
  name: string | null;
  operator: string | null;
  dist_m: number | null;
  n_deliveries: number;
  deliveries: OnshoreDelivery[];
}

export interface OnshorePlant {
  id: string;
  name: string;
  org_nr: string | null;
  address: string | null;
  postnr: string | null;
  poststed: string | null;
  kommune: string | null;
  prodform: string | null;
  slaughter_farmed: boolean;
  slaughter_marine: boolean;
  kind: OnshoreKind;
  geocode_quality: string | null;
  lon: number | null;
  lat: number | null;
  days: number;
  n_vessels: number;
  n_arrivals_with_origin: number;
  by_role: Record<string, number>;
  vessels: OnshoreVessel[];
  origin_farms: OnshoreOriginFarm[];
  operators: OnshoreOperator[];
  holding_cages: OnshoreHoldingCage[];
}

export interface OnshoreArrivals {
  type: "FeatureCollection";
  count: number;
  features: GeoJSON.Feature[];
}

export function getOnshorePlant(id: string, days = 7): Promise<OnshorePlant> {
  return get(`/v1/onshore/${encodeURIComponent(id)}?days=${days}`);
}

export function getOnshoreArrivals(id: string, days = 7): Promise<OnshoreArrivals> {
  return get(`/v1/onshore/${encodeURIComponent(id)}/arrivals?days=${days}`);
}

/** URL of the high-res NIB ortophoto for an onshore plant. */
export function onshoreImageUrl(id: string, box: 500 | 1000 | 2000 = 1000): string {
  return `${BASE}/v1/onshore/${encodeURIComponent(id)}/nib-image?box=${box}`;
}

/* ---- Vessel profile (role & network on the vessel page) ----------------- */

export interface VesselProfileApi {
  mmsi: number;
  name: string;
  length_m: number | null;
  aqua_beh: boolean;
  role: "smolt carrier" | "harvest wellboat" | "delousing / treatment" | "service / feed";
  n_smolt: number;
  n_delousing: number;
  n_harvest: number;
  n_service: number;
  n_farms: number;
  operating_hours: number;
  uptime: number; // 0-100
  workdays_per_month: number;
  span_days: number;
  first_op: string;
  last_op: string;
}

export function getVesselProfile(mmsi: number): Promise<VesselProfileApi> {
  return get(`/v1/vessels/${mmsi}/profile`);
}

/* ---- Site operations log + status (location page) ------------------------ */

export interface SiteOpEvent {
  event_date: string;
  event_type:
    | "smolt_in"
    | "smolt_suspected"
    | "smolt_delivery"
    | "delousing"
    | "delousing_suspected"
    | "harvest"
    | "harvest_topup"
    | "harvest_suspected"
    | "ensilage"
    | "activity_unknown";
  method: string | null;
  method_basis: "fdir" | "dwell" | "cohort" | "ais_model" | "ais_only" | null;
  vessel_name: string | null;
  mmsi: number | null;
  dwell_min: number | null;
  to_slaughter: boolean | null;
  days_to_slaughter: number | null;
  confirmed: boolean;
  n_vessels: number | null; // peak co-present vessels (flotilla size)
  confidence: number | null; // AIS-model probability for delousing_suspected
  // Over-treatment flag (delousing only): young cohort treated at low lice.
  over_treatment?: boolean | null;
  benefit_prob?: number | null; // ~P(meaningful lice benefit); low ⇒ wasteful
  lice_at_event?: number | null;
  cohort_age_mo?: number | null;
  // Provenance of the haul (from iq_event_endpoints): for smolt_in/smolt_delivery
  // the settefisk ORIGIN — site, operating company (+ slug for deep-link), carrier
  // vessel, voyage hours and acclimatization mode; for harvest the DESTINATION plant.
  // origin = smolt from a settefisk hatchery (link /s/); transfer = older fish
  // moved in from another grow-out site (link /l/); destination = harvest plant.
  endpoint_role?: "origin" | "transfer" | "destination" | null;
  endpoint_site?: string | null;
  endpoint_site_loc?: number | null; // settefisk (origin) or grow-out (transfer) locality_no
  endpoint_company?: string | null;
  endpoint_company_slug?: string | null;
  endpoint_vessel?: string | null;
  endpoint_vessel_mmsi?: number | null;
  endpoint_travel_h?: number | null;
  endpoint_accl_mode?: "direct" | "in_transit" | null;
  endpoint_move_risk?: "high" | "elevated" | "low" | null; // for transfers
}

export interface SiteOpSummary {
  last_cohort_open: string | null;
  age_months: number | null;
  // cohort-chain corrections (new): true age from the originating hatchery,
  // following transfers back; origin of the current cohort; the cohort id.
  age_months_true?: number | null;
  origin_kind?: "settefisk" | "transfer" | "unknown" | null;
  origin_loc?: number | null;
  origin_site?: string | null;
  origin_move_risk?: "high" | "elevated" | "low" | null;
  cohort_id?: number | null;
  est_harvest_start: string | null;
  est_harvest_end: string | null;
  cur_lice: number | null;
  sea_temp_c: number | null;
  theta_trigger: number | null;
  delousing_status: "ok" | "watch" | "due" | "monitor" | "fallow";
  delousing_reason: string | null;
  last_delousing: string | null;
  last_delousing_method: string | null;
  n_delousing_12mo: number;
}

export interface SiteOperations {
  locality_no: number;
  summary: SiteOpSummary | null;
  count: number;
  events: SiteOpEvent[];
}

export function getSiteOperations(
  localityNo: number,
  since = "730d",
  limit = 80,
): Promise<SiteOperations> {
  return get(`/v1/receivers/${localityNo}/operations?since=${since}&limit=${limit}`);
}

export interface SiteWellboat {
  mmsi: number;
  name: string;
  role: string;
  uptime: number;
  workdays_per_month: number;
  dist_km: number;
  sog: number;
  ts: string;
  last_op: string | null;
  last_op_at: string | null;
  status: "transiting" | "working" | "idle";
}

export interface SiteWellboats {
  locality_no: number;
  radius_km: number;
  count: number;
  vessels: SiteWellboat[];
}

export function getSiteWellboats(
  localityNo: number,
  radiusKm = 150,
  limit = 12,
): Promise<SiteWellboats> {
  return get(`/v1/receivers/${localityNo}/wellboats?radius_km=${radiusKm}&limit=${limit}`);
}

/* ---- Settefisk (smolt hatchery) dispatch -------------------------------- */

export interface SettefiskVoyage {
  mmsi: number;
  vessel: string | null;
  length_m: number | null;
  arrive_origin: string | null;
  depart_origin: string | null;
  dwell_origin_min: number | null;
  dest_loc: number;
  dest_site: string | null;
  dest_municipality: string | null;
  arrive_dest: string | null;
  voyage_h: number | null;
  accl_mode: "direct" | "in_transit" | null;
  dest_operator: string | null;
  dest_operator_slug: string | null;
}

export interface SettefiskCaller {
  mmsi: number;
  vessel: string | null;
  length_m: number | null;
  aqua_fleet: boolean | null;
  n_visits: number;
  total_dwell_min: number | null;
  last_visit: string | null;
  n_deliveries: number;
}

export interface SettefiskDestination {
  dest_loc: number;
  dest_site: string | null;
  dest_municipality: string | null;
  dest_operator: string | null;
  dest_operator_slug: string | null;
  n_voyages: number;
  last_dispatch: string | null;
}

export interface SettefiskSummary {
  n_voyages: number;
  n_vessels: number;
  n_destinations: number;
  n_callers: number;
  in_transit_share: number | null;
  first: string | null;
  last: string | null;
}

export interface SettefiskSite {
  locality_no: number;
  name: string;
  municipality: string | null;
  lat: number | null;
  lon: number | null;
  receiver_id: string | null; // UUID, for the high-res NIB ortophoto
  operator: string | null;
  operator_slug: string | null;
  license_nr: string | null;
  capacity: number | null;
  capacity_unit: string | null;
  species: string | null;
  summary: SettefiskSummary;
  voyages: SettefiskVoyage[];
  vessels: SettefiskCaller[];
  destinations: SettefiskDestination[];
}

export function getSettefisk(localityNo: number): Promise<SettefiskSite> {
  return get(`/v1/settefisk/${localityNo}`);
}

/* ---- Cohort chain (hatchery → net → net → slaughterhouse) --------------- */

export interface CohortSegment {
  segment_id: number;
  locality_no: number;
  site: string | null;
  municipality: string | null;
  lat: number | null;
  lon: number | null;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean | null;
  origin_kind: "settefisk" | "transfer" | "unknown" | null;
  origin_loc: number | null;
  origin_site: string | null;
  origin_segment_id: number | null;
  origin_mmsi: number | null;
  origin_vessel: string | null;
  origin_date: string | null;
  move_risk: "high" | "elevated" | "low" | null;
  n_delousings: number | null;
  n_transfers_out: number | null;
  last_lice: number | null;
  max_lice: number | null;
  harvested: boolean | null;
  harvest_date: string | null;
  harvest_plant: string | null;
  operator: string | null;
  operator_slug: string | null;
}

export interface CohortHatchery {
  locality_no: number;
  name: string;
  lat: number | null;
  lon: number | null;
  operator: string | null;
  operator_slug: string | null;
}

export interface CohortMetrics {
  po_id: number | null;
  po_name: string | null;
  n_peers: number | null;
  age_months: number | null;
  handling: number | null;
  n_delousings: number | null;
  n_moves: number | null;
  peak_lice: number | null;
  disease_weeks: number | null;
  mean_temp_c: number | null;
  noise_db: number | null;
  worst_move_risk: "high" | "elevated" | "low" | null;
  pct_handling: number | null;
  pct_delousings: number | null;
  pct_moves: number | null;
  pct_lice: number | null;
  pct_disease: number | null;
  pct_temp: number | null;
  pct_noise: number | null;
}

/** This cohort's age-adjusted percentile within one peer sub-group (the
 *  company's own cohorts, or the site's own history). Null pct = too few peers
 *  with that metric to rank. */
export interface CohortPeerGroup {
  n: number;
  pct_handling: number | null;
  pct_lice: number | null;
  pct_disease: number | null;
  pct_temp: number | null;
  pct_noise: number | null;
}
export interface CohortPeerGroups {
  cohort_id: number;
  company: (CohortPeerGroup & { operator: string | null; slug: string | null }) | null;
  site: (CohortPeerGroup & { site: string | null; n_sites: number }) | null;
}

export function getCohortPeerGroups(rootId: number): Promise<CohortPeerGroups> {
  return get(`/v1/cohort/${rootId}/peer-groups`);
}

/** A cohort's daily underwater-noise exposure over its life (one value/day,
 *  following site moves) + the accumulated sound-exposure dose. `series` may be
 *  sparse while the 5-year backfill is still running (see `coverage`). */
export interface CohortNoiseSeries {
  cohort_id: number;
  start_day: string | null;
  end_day: string | null;
  life_days: number;
  covered_days: number;
  coverage: number;
  mean_leq_db: number | null;
  accumulated_db: number | null;
  series: { day: string; leq_db: number }[];
  /** This cohort's age now, in whole months from start. */
  age_month_now: number | null;
  /** Where this cohort's accumulated dose sits among all cohorts AT THIS AGE
   *  (0..1, fraction at or below it). null if too few peers at that age. */
  percentile: number | null;
  /** Number of cohorts in the distribution at this cohort's current age. */
  peer_n: number | null;
  /** The cross-cohort "scenario fan": accumulated-dose percentiles per age-month
   *  across every cohort that reached that age. Read this cohort's own curve
   *  against it like a growth chart. */
  reference: {
    age_month: number;
    n: number;
    q10: number;
    q25: number;
    q50: number;
    q75: number;
    q90: number;
  }[];
}

export function getCohortNoiseSeries(rootId: number): Promise<CohortNoiseSeries> {
  return get(`/v1/cohort/${rootId}/noise-series`);
}

/** NEAR-FIELD operational acoustic exposure — the noise the salmon ACTUALLY
 *  experiences, dominated by work vessels within tens of metres of the cage
 *  (well-boats, de-lousing/service craft). The transit-noise series suppresses
 *  these (speed-based source level, moored penalty), so this is a separate axis.
 *  Proximity-weighted proxy from AIS dwell, not a calibrated SPL. Doubles as a
 *  handling-stress signal. */
export interface CohortNearfieldSeries {
  cohort_id: number;
  start_day: string | null;
  end_day: string | null;
  index_total: number;
  index_per_month: number;
  work_dwell_hours: number;
  n_close_visits: number;
  method: string;
  note: string;
  series: { day: string; index: number; dwell_min: number }[];
}

export function getCohortNearfieldSeries(rootId: number): Promise<CohortNearfieldSeries> {
  return get(`/v1/cohort/${rootId}/nearfield-series`);
}

/** Near-field as a GROWTH CHART against the BEST cohorts in the same production
 *  zone (lowest age-adjusted lice residual), not the median of all. Accumulated
 *  near-field vs fish age: percentile bands of the cleanest cohorts + this
 *  cohort's own curve, plus the age from which it departs the typical-clean
 *  range. Shows how far above best-in-class the fish-experienced noise runs. */
export interface CohortNearfieldBand {
  cohort_id: number;
  po_name: string;
  n_best: number;
  best_resid_max: number | null;
  cross_age: number | null;
  grid: number[];
  q10: number[];
  q25: number[];
  q50: number[];
  q75: number[];
  q90: number[];
  focal: (number | null)[];
  note: string;
}

export function getCohortNearfieldBand(rootId: number): Promise<CohortNearfieldBand> {
  return get(`/v1/cohort/${rootId}/nearfield-band`);
}

/** Short-horizon lice now-cast: from the cohort's recent weekly adult-female
 *  lice, when does it cross the 0.1 / 0.2 / 0.5 action thresholds? Deliberately
 *  NOT a per-site "R0" (a mixed-effects analysis found no reproducible site
 *  signal); the validated skill is the current level + recent slope, good only
 *  at a 1-3 week horizon. Per-threshold: status, predicted weeks + a band. */
/** The binding regulatory limit over the horizon. The limit steps with the
 *  calendar (0.5 year-round → 0.2 spring weeks 16-21 → 0.1 if the production area
 *  is red), so `kind` says which rule is biting and `threshold` its value. */
export interface CohortLiceBinding {
  threshold: number;
  kind: "general" | "spring" | "spring_red";
  status: "above" | "rising" | "stable" | "falling" | "beyond_horizon";
  weeks: number | null;
  weeks_lo: number | null;
  weeks_hi: number | null;
  confidence: "high" | "medium" | "low" | "none" | "n/a";
}
export interface CohortLiceForecast {
  cohort_id: number;
  active: boolean;
  status: "ok" | "no_data";
  current: number | null;
  as_of: string | null;
  trend: "rising" | "stable" | "falling" | null;
  slope_per_week: number | null;
  doubling_weeks: number | null;
  leg_points?: number;
  po_name: string | null;
  po_colour: string | null;
  in_spring_window: boolean | null;
  binding_now: number | null;
  spring_threshold: number | null;
  general_threshold: number;
  binding: CohortLiceBinding | null;
  series: { day: string; lice: number; treated: boolean }[];
  /** Pre-adult stages ON this cohort's own site — the immature lice that mature
   *  into the regulated adult-female count. A leading indicator that an
   *  adult-female-only series (which can read flat at zero) cannot show. */
  stages?: { day: string; mobile: number | null; stationary: number | null }[];
  /** Adult-female pressure on the OTHER sites in this cohort's production area —
   *  the external infection load a self-growth model cannot see. */
  area?: {
    name: string | null;
    colour: string | null;
    threshold: number;
    weeks: { day: string; median: number | null; max: number | null; n_at_limit: number; n_sites: number }[];
    latest: { day: string; median: number | null; max: number | null; n_at_limit: number; n_sites: number } | null;
  } | null;
  /** 6-week stage-structured projection of adult females — blends the validated
   *  momentum path with a maturation+area-recruitment model, so it can rise from a
   *  near-zero count. Carries the recommended delousing window (opens ahead of the
   *  projected limit crossing, closes at the 'treat-by' deadline). */
  projection?: LiceProjection | null;
}

export interface LiceProjection {
  as_of: string;
  horizon_weeks: number;
  temp_c: number | null;
  threshold: number;
  area_pressure: number | null;
  points: { week: number; day: string; central: number; lo: number; hi: number }[];
  crossing_week: number | null;
  crossing_week_earliest: number | null;
  action: "plan_treatment" | "watch_closely" | "watch";
  driver: "momentum" | "buildup";
  treat_window: { start_week: number; end_week: number; start: string; end: string; lead_weeks: number } | null;
  recruit_per_week: number;
  matured_mobile_frac: number;
  seed: { adult: number; mobile: number; stationary: number; stage_age_weeks: number };
  params: Record<string, number>;
  grounded: string;
  flagged: string;
}

export function getCohortLiceForecast(rootId: number): Promise<CohortLiceForecast> {
  return get(`/v1/cohort/${rootId}/lice-forecast`);
}

/** PD (pancreas disease / SAV) early-warning. Unlike lice and ILA, PD has a real,
 *  placebo-validated 0-5 km farm-to-farm contagion signal plus a mechanical-
 *  delousing-stress driver, so a transparent logistic hazard (OOS weekly AUC
 *  ~0.81) gives a usable relative risk score. Two actionable levers: active PD in
 *  a <5 km neighbour, and recent mechanical handling. */
export interface CohortPdForecast {
  cohort_id: number;
  status: "ok" | "no_data";
  active?: boolean;
  as_of?: string;
  pd_active?: boolean;
  risk_level?: "low" | "elevated" | "high" | "active";
  prob_4wk?: number;
  baseline_4wk?: number;
  multiplier?: number;
  oos_auc?: number;
  drivers?: {
    neighbor_pd_weeks: number;
    n_neighbors_5km: number;
    infected_neighbors: { name: string; km: number; last_pd: string | null }[];
    mechanical_8wk: number;
    temp_c: number;
    season: string;
    lice: number;
    age_weeks: number;
  };
}

export function getCohortPdForecast(rootId: number): Promise<CohortPdForecast> {
  return get(`/v1/cohort/${rootId}/pd-forecast`);
}

/** A likely UNREPORTED delousing: a vessel sat at the site and lice crashed the
 *  same week, but no treatment was officially reported. Triangulated from AIS
 *  operations + FDIR weekly lice/treatment reports (conservative definition). */
export interface SuspectedTreatment {
  locality_no: number;
  event_date: string;
  event_type: string;
  vessel_name: string | null;
  mmsi: number | null;
  dwell_hours: number | null;
  n_vessels: number | null;
  lice_before: number | null;
  lice_after: number | null;
  lice_drop_frac: number | null;
}

export function getCohortSuspectedTreatments(
  rootId: number,
): Promise<{ cohort_id: number; events: SuspectedTreatment[] }> {
  return get(`/v1/cohort/${rootId}/suspected-treatments`);
}

export function getSiteSuspectedTreatments(
  localityNo: number,
): Promise<{ locality_no: number; events: SuspectedTreatment[] }> {
  return get(`/v1/receivers/${localityNo}/suspected-treatments`);
}

export interface CohortChain {
  cohort_id: number;
  metrics: CohortMetrics | null;
  focus_loc: number | null;
  true_start_date: string | null;
  hatchery: CohortHatchery | null;
  origin_kind: "settefisk" | "transfer" | "unknown" | null;
  n_sites: number;
  n_moves: number;
  n_delousings: number;
  worst_move_risk: "high" | "elevated" | "low" | null;
  harvested: boolean;
  harvest_plant: string | null;
  harvest_date: string | null;
  segments: CohortSegment[];
}

export function getSiteCohort(localityNo: number): Promise<{ locality_no: number; cohort: CohortChain | null }> {
  return get(`/v1/receivers/${localityNo}/cohort`);
}

export function getCohortChain(rootId: number): Promise<CohortChain> {
  return get(`/v1/cohort/${rootId}`);
}

/* ---- Cohort lists (every cohort over time, on a site or a company) ------- */

/** One production cycle at a site → a link to its full cohort page. */
export interface SiteCohortItem {
  cohort_id: number;
  segment_id: number;
  start_date: string | null;
  true_start_date: string | null;
  is_active: boolean | null;
  origin_kind: "settefisk" | "transfer" | "unknown" | null;
  origin_loc: number | null;
  origin_site: string | null;
  origin_date: string | null;
  move_risk: "high" | "elevated" | "low" | null;
  n_delousings: number | null;
  max_lice: number | null;
  last_lice: number | null;
  harvested: boolean | null;
  harvest_date: string | null;
  harvest_plant: string | null;
  age_months_true: number | null;
}

/** One cohort that touched a company's sites → a link to its cohort page. */
export interface OperatorCohortItem {
  cohort_id: number;
  current_loc: number | null;
  current_site: string | null;
  is_active: boolean | null;
  true_start_date: string | null;
  harvested: boolean | null;
  harvest_date: string | null;
  harvest_plant: string | null;
  origin_kind: "settefisk" | "transfer" | "unknown" | null;
  origin_loc: number | null;
  origin_site: string | null;
  worst_move_risk: "none" | "low" | "elevated" | "high" | null;
  age_months_true: number | null;
  company_locs: number[] | null;
  pct_lice: number | null;
  pct_handling: number | null;
  pct_disease: number | null;
  n_peers: number | null;
  po_name: string | null;
}

export function getSiteCohorts(localityNo: number): Promise<{
  locality_no: number;
  count: number;
  cohorts: SiteCohortItem[];
}> {
  return get(`/v1/receivers/${localityNo}/cohorts`);
}

export function getOperatorCohorts(slug: string): Promise<{
  slug: string;
  name: string;
  count: number;
  active: OperatorCohortItem[];
  past: OperatorCohortItem[];
}> {
  return get(`/v1/operators/${encodeURIComponent(slug)}/cohorts`);
}

/** Which of an operator's active cohorts need attention now (lice nearing/over
 *  the binding limit, active PD/ILA, PD early-warning). One sorted list for the
 *  company page; each item links to its cohort. */
export interface AttentionIssue {
  label: string;
  detail: string;
  tone: "crit" | "warn" | "ok";
}
export interface AttentionItem {
  cohort_id: number;
  locality_no: number;
  site: string | null;
  as_of: string;
  severity: number;
  lice: number;
  issues: AttentionIssue[];
}
export function getOperatorAttention(slug: string): Promise<{
  operator: string;
  count: number;
  items: AttentionItem[];
}> {
  return get(`/v1/operators/${encodeURIComponent(slug)}/attention`);
}

/* ---- Fjordle (daily game) ----------------------------------------------- */

export interface LeaderRow {
  name: string;
  score: number;
  distance_km: number | null;
  hints: number;
}
export function getLeaderboard(day: number): Promise<{ players: number; items: LeaderRow[] }> {
  return get(`/v1/game/leaderboard?game=fjordle&day=${day}&limit=20`);
}
export async function postScore(body: {
  day: number;
  name: string;
  score: number;
  distance_km: number;
  hints: number;
}): Promise<{ best: number; rank: number; players: number }> {
  const r = await fetch(`${BASE}/v1/game/score`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ game: "fjordle", ...body }),
  });
  if (!r.ok) throw new ApiError(r.status, "/v1/game/score");
  return r.json();
}

/* ---- Mortality / delousing decision tool -------------------------------- */

/** Inputs for the mortality decision model. Supply a measured `fish_weight_g`
 *  if known, otherwise `age_months` (months since sea transfer). */
export interface MortalityDecisionInput {
  water_temp_c: number;
  fish_weight_g?: number | null;
  age_months?: number | null;
  salinity_psu?: number | null;
  delousing_type?: string | null;
  days_since_last_treatment?: number | null;
  treatments_this_month?: number;
  pd_active?: boolean;
  disease_flags?: string[] | null;
  /** Calendar month 1-12; enables the optimal-timing projection. */
  current_month?: number | null;
  /** Weeks until lice cross the binding limit (the treat-by deadline). */
  weeks_to_lice_limit?: number | null;
  /** 12 real per-site monthly mean SST °C (Jan..Dec) for the timing projection. */
  monthly_sst?: number[] | null;
}

/** One method's cost/timing over the projected temperature trajectory. */
export interface MortalityMethodTiming {
  acute_profile: string;
  method_class: "medicinal" | "non_medicinal";
  now_pct: number;
  best_week: number;
  best_pct: number;
  saving_pct: number;
}

/** Cheapest feasible delousing timing before the lice limit forces a treatment. */
export interface MortalityTiming {
  action: "wait" | "treat_now";
  sst_source: "site" | "national_default";
  deadline_weeks: number | null;
  horizon_weeks: number;
  best_week: number;
  best_temp_c: number;
  lead_method: string;
  now_pct: number;
  best_pct: number;
  saving_pct: number;
  per_method: MortalityMethodTiming[];
  trajectory: { week: number; temp_c: number }[];
  note: string;
}

/** One delousing method's acute cost + resulting health score at these
 *  conditions (lets the UI switch method with no re-fetch). */
export interface MortalityMethod {
  acute_profile: string;
  method_class: "medicinal" | "non_medicinal";
  excess_mortality_pct: number;
  treat_now_pct: number;
  extra_mortality_from_treating_pct: number;
  health_score: number;
}

/** Robust mortality/delousing decision output — chronic baseline, the acute
 *  cost of delousing now (per method), a treat-now vs do-nothing comparison,
 *  a 0-100 health score, and evidence-backed recommendations. Built only from
 *  Oliveira 2021 + Tvete 2023; lice COUNT is deliberately not scored. */
export interface MortalityDecision {
  model_version: string;
  inputs: {
    water_temp_c: number;
    fish_weight_g: number;
    weight_source: "measured" | "estimated_from_age";
    disease_flags: string[];
  };
  chronic: {
    monthly_mortality_per_1000: number;
    monthly_mortality_pct: number;
    daily_mortality_pct: number;
    temp_multiplier: number;
    pd_multiplier: number;
  };
  acute_treatment: {
    delousing_type: string;
    acute_profile: string;
    window_days: number;
    excess_mortality_pct: number;
    peak_daily_excess_pct: number;
    curve: { day: number; cumulative_excess_pct: number; daily_excess_pct: number }[];
  } | null;
  method_comparison: MortalityMethod[];
  optimal_timing: MortalityTiming | null;
  compare: {
    window_days: number;
    do_not_treat_pct: number;
    treat_now_pct: number;
    extra_mortality_from_treating_pct: number;
  };
  health_score: {
    score: number;
    band: "good" | "fair" | "elevated" | "poor";
    components: Record<string, number>;
  };
  recommendations: string[];
  caveats: string[];
  sources: Record<string, string>;
}

/** Real per-site monthly SST climatology (°C, Jan..Dec) for a cohort's current
 *  locality — feeds the decision's optimal-timing projection. `months` entries
 *  may be null for a calendar month with no historical data. */
export interface CohortSstClimatology {
  cohort_id: number;
  locality_no: number | null;
  months: (number | null)[];
  complete: boolean;
  years: [number | null, number | null] | null;
  source: string;
}

export function getCohortSstClimatology(rootId: number): Promise<CohortSstClimatology> {
  return get(`/v1/cohort/${rootId}/sst-climatology`);
}

/* ---- Delousing scenario matrix (economic · welfare · logistics) --------- */

export interface ScenarioRow {
  key: string;
  label: string;
  method: string | null;
  method_class: "medicinal" | "non_medicinal" | null;
  n_treatments: number;
  mortality_pct: number | null;
  lost_tonnes: number | null;
  mortality_cost_nok: number;
  operation_cost_nok: number;
  economic_cost_nok: number;
  vessels: number;
  op_days: number;
  co2_t: number;
  welfare_score: number;
  compliant: boolean;
  valid: boolean;
  note?: string;
}
export interface ScenarioColumn {
  key: string;
  label: string;
  unit: string;
  grounded: boolean | "partial" | "heuristic";
}
export interface DelousingScenarios {
  cohort_id: number;
  status?: "no_age" | "no_biomass";
  locality_no: number;
  harvest_source: "harvest_forecast" | "standing_biomass";
  price_as_of: string | null;
  current_lice: number | null;
  fish_weight_g: number;
  water_temp_c: number;
  harvest_tonnes: number;
  price_nok_per_kg: number;
  harvest_value_nok: number;
  lice_status: "below" | "rising" | "above";
  assumptions: Record<string, number>;
  assumptions_flagged: string[];
  columns: ScenarioColumn[];
  rows: ScenarioRow[];
  optimal: Record<string, string | null>;
  caveats: string[];
}

export function getCohortDelousingScenarios(rootId: number): Promise<DelousingScenarios> {
  return get(`/v1/cohort/${rootId}/delousing-scenarios`);
}

export async function postMortalityDecision(
  body: MortalityDecisionInput,
): Promise<MortalityDecision> {
  const r = await fetch(`${BASE}/v1/mortality/decision`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new ApiError(r.status, "/v1/mortality/decision");
  return r.json();
}
